import KeyPath from './KeyPath'
import Immutable from 'immutable'
import * as ElementTypes from '../constants/ElementTypes'
import * as LinkTypes from '../constants/LinkTypes'
import { EDIT_MODES } from '../constants/EditModes'
import {CSS_SAFE_FONTS} from '../constants/Fonts'

export function getElementAtPath(props, path) {
    if (!props || !path) {
        return null
    }

    const model = getModel(path, props)
    if (!model) {
        return null
    }

    return model.getIn(new KeyPath(path).toArray())
}

export function getElementParent(props, path) {
    const model = getModel(path, props)
    const keyPath = new KeyPath(path).parent.parent
    return model.getIn(keyPath.toArray())
}

export function getSelectedElement(props) {
    const selectedPath = props.editorState.get('selected')
    if (selectedPath && selectedPath !== '0') {
        return getElementAtPath(props, selectedPath)
    }
    return null
}

export function getModel(path, {pageModel, siteModel}) {
    const keyPath = new KeyPath(path)
    switch (keyPath.topElement) {
        case 'pageData':
            return pageModel
        case 'siteData':
            return siteModel
    }
}

export function elementTypeFriendlyName(nodeType, templateName = null, inputType = null) {
    const inputTypeMapping = {
        'file': 'file upload',
        'text': 'text field',
        'email': 'email field',
        'number': 'number field',
        'tel': 'phone field',
        'date': 'date field',
        'time': 'time field'
    }
    switch (nodeType) {
        case ElementTypes.COLUMN:
            return 'column'
        case ElementTypes.SECTION:
            return 'section'
        case ElementTypes.INLINE_APP:
            const json = templateName ? require('json-loader!../inline_apps.json') : null
            return json ? json[templateName].title : 'app'
        case ElementTypes.REQUIRED_LABEL:
            return 'required label'
        case ElementTypes.RADIO_BUTTON_GROUP:
            return 'radio button group'
        case ElementTypes.CHECKBOX_GROUP:
            return 'checkbox group'
        case ElementTypes.FORM_BUTTON:
            return 'form button'
        case ElementTypes.TEXTAREA:
            return 'text area'
        case ElementTypes.SOCIAL_ICONS:
            return 'social icons'
        case ElementTypes.SELECT:
            return 'dropdown list'
        case ElementTypes.INPUT:
            return inputType ? inputTypeMapping[inputType] : nodeType
        default:
            return nodeType
    }
}

// Returns the sub directory name for the partner
export function getPartnerPath(props) {
    let partnerCode = props.siteSettingsModel.getIn(['userSettings', 'partner_code'])
    if (!partnerCode || partnerCode === 'moonfruit') {
        return ''
    }
    return partnerCode + '/'
}

function getProperty(props, propName, path) {
    if (path && path !== '0') {
        const keyPathArr = new KeyPath(path).toArray()
        let element = getModel(path, props).getIn([...keyPathArr])
        return element ? element.get(propName) : ''
    }
    return ''
}

export function getModeStyleKey(editMode) {
    return editMode ? ('style-' + editMode) : 'style'
}

function getStyleProperty(props, propName, path, editMode) {
    const styleMode = getModeStyleKey(editMode);

    if (path && path !== '0') {
        const styleProps = getModel(path, props).getIn([...new KeyPath(path).toArray(), styleMode])
        return styleProps ? styleProps.get(propName) : ''
    }
    return ''
}


export function getStylePropertyClass(props, property, path, editMode) {
    return getClass(props, property, path, editMode)
}

function getClass(props, className, path, editMode) {
    let classes = getProperty(props, 'classes', path)
    if (classes) {
        const regex = editMode
            ? `^${className}-${editMode}`
            : `^${className}-`
        const re = new RegExp(regex)

        classes = classes.toJSON()
        return classes.find((c) => {
            return editMode
                ? c.match(re)
                : c.match(re) && !EDIT_MODES.find((edit) => {
                    return c.match(new RegExp(regex + `${edit}-`))
                })
        })
    }
    return ''
}

export function updateStylePropertyClass(props, property, className, path) {

    const re = new RegExp("^" + property + "-")
    const elementClasses = getProperty(props, 'classes', path)
    const oldClasses = elementClasses ? elementClasses.toJSON() : []

    const newClasses = oldClasses.filter(x => !x.match(re))
    if (className) { // If not, it will be removed
        newClasses.push(className)
    }
    return newClasses
}

export function getSelectedProperty(props, propName) {
    const selectedPath = props.editorState.get('selected');
    return getProperty(props, propName, selectedPath);
}

export function getSelectedStyleProperty(props, propName, editMode) {
    const selectedPath = props.editorState.get('selected');
    return getStyleProperty(props, propName, selectedPath, editMode);
}

export function getSelectedBackgroundImageStyleProperty(props) {
    const selectedPath = props.editorState.get('selected');

    const backgroundImage = getStyleProperty(props, 'backgroundImage', selectedPath);
    if (backgroundImage) {
        return backgroundImage;
    }

    const background = getStyleProperty(props, 'background', selectedPath);
    if (background) {
        return background.match(/url\(([^)]+)\)/)[1];
    }
}

export function getSelectedStylePropertyClass(props, property, editMode) {
    const selectedPath = props.editorState.get('selected');
    return getStylePropertyClass(props, property, selectedPath, editMode);
}

export function getSelectedClass(props, className, editMode) {
    const selectedPath = props.editorState.get('selected');
    return getClass(props, className, selectedPath, editMode);
}

export function updateSelectedStylePropertyClass(props, property, className, editMode) {
    const classes = getSelectedProperty(props, 'classes');
    const oldClasses = classes ? classes.toJSON() : [];
    return setStylePropertyClass(oldClasses, property, className, editMode);
}

export function setStylePropertyClass(classList, property, newClass, editMode) {
    const regex = editMode
        ? `^${property}-${editMode}-`
        : `^${property}-`
    const re = new RegExp(regex)
    const newClasses = classList.filter((c) => {
        return editMode
            ? !c.match(re)
            : !c.match(re) || EDIT_MODES.find((edit) => {
                return c.match(new RegExp(regex + `${edit}-`))
            })
    });
    if (newClass) { // If not, it will be removed
        newClasses.push(newClass)
    }
    return newClasses
}


export function updateSelectedClasses(props, classNames, remove = false) {
    const elementClasses = getSelectedProperty(props, 'classes')
    const oldClasses = elementClasses ? elementClasses.toJSON() : []

    // return all classes without the ones we are setting
    //  if !remove the classes will be added, else will be removed
    const newClasses = oldClasses.filter(x => !classNames.find(className => className === x))
    if (!remove) {
        classNames.forEach(className => newClasses.push(className))
    }
    return newClasses
}

export function copyTextAlignStyleToClass(props) {
    const allStyles = props.style;
    const path = props.pathToMe;
    const alignClass = getStylePropertyClass(props, 'align', path);

    let classes;

    if (alignClass) {
        classes = getProperty(props, 'classes', path);
    } else {
        const updatedAlignClass = (allStyles && allStyles.textAlign)
            ? 'align-' + allStyles.textAlign
            : 'align-inherit';

        classes = updateStylePropertyClass(props, 'align', updatedAlignClass, path);
    }

    return classes;
}

export function getAllSectionIds(props) {
    const headerSectionIds = props.siteModel.getIn(['siteData', 'header', 'containers']).map(c => c.get('id') || c.get('_key'))
    const footerSectionIds = props.siteModel.getIn(['siteData', 'footer', 'containers']).map(c => c.get('id') || c.get('_key'))
    const pageSectionIds = props.pageModel.getIn(['pageData', 'containers']).map(c => c.get('id') || c.get('_key'))
    const sectionIds = [...headerSectionIds, ...pageSectionIds, ...footerSectionIds]

    return sectionIds
}

export function getHrefForComponent(props) {
    const sectionIds = getAllSectionIds(props)
    const linkType = props.node.get('linkType')
    switch (linkType) {
        case LinkTypes.EMAIL:
            return props.node.get('href') ? 'mailto:' + props.node.get('href') : ''
        case LinkTypes.TOP_OF_THIS_PAGE:
            return '#' + sectionIds[0]
        case LinkTypes.BOTTOM_OF_THIS_PAGE:
            let last = sectionIds.length - 1
            return '#' + sectionIds[last]
        case LinkTypes.CLICK_TO_CALL:
            return props.node.get('href') ? 'tel:' + props.node.get('href') : ''
        default:
            return props.node.get('href') || ''
    }
}


export function fromJSGreedy(js) {
    return typeof js !== 'object' || js === null || Immutable.Iterable.isIterable(js) ? js :
        Array.isArray(js)
            ? Immutable.Seq(js).map(fromJSGreedy).toList()
            : Immutable.Seq(js).map(fromJSGreedy).toMap()
}

export function openLeftSidebar(e, editorStateProperty, props) {
    e.stopPropagation()
    props.actions.setEditorStateProperty('isSideBarOpen', false)

    const toggleProperties = {
        'isLeftSidebarOpen': true,
        'isSiteSettingsOpen': false,
        'isMediaManagerOpen': false,
        'isAddBlockPanelOpen': editorStateProperty === 'isAddBlockPanelOpen',
        'isPageManagerOpen': editorStateProperty === 'isPageManagerOpen',
        'isAddElementPanelOpen': editorStateProperty === 'isAddElementPanelOpen',
    }

    Object.keys(toggleProperties).forEach(function(key) {
        if (props.editorState.get(key) !== toggleProperties[key]) {
            props.actions.setEditorStateProperty(key, toggleProperties[key])
        }
    })
}

// inspired by joshcanhelp/scrollTo.js
// https://gist.github.com/joshcanhelp/a3a669df80898d4097a1e2c01dea52c1
// which itself was inspired by:
// Smooth scroll-to inspired by:
// http://stackoverflow.com/a/24559613/728480
//

export function scrolling(scrollTo, elem, scrollDuration = 1000) {
    let cosParameter = (elem.scrollTop - scrollTo) / 2,
        scrollCount = 0,
        oldTimestamp = window.performance.now();

    function step(newTimestamp) {
        const tsDiff = newTimestamp - oldTimestamp;

        scrollCount += Math.PI / (scrollDuration / tsDiff);
        if (scrollCount >= Math.PI) {
            return;
        }
        let moveStep = Math.round(scrollTo + cosParameter + cosParameter * Math.cos(scrollCount))
        elem.scrollTop = moveStep;
        oldTimestamp = newTimestamp;
        window.requestAnimationFrame(step);
    }
    window.requestAnimationFrame(step);
}

export function extractFonts(parent) {
    let container = [];

    function extract(children) {
        if (children.style) {

            if (children.style.fontFamily) {
                container.push(children.style.fontFamily);
            }

            if (children.style.h1 && children.style.h1.fontFamily) {
                container.push(children.style.h1.fontFamily);
            }

            if (children.style.h2 && children.style.h2.fontFamily) {
                container.push(children.style.h2.fontFamily);
            }

            if (children.style[".logo"] && children.style[".logo"].fontFamily) {
                container.push(children.style[".logo"].fontFamily);
            }

            if (children.style[".dropdown-menu"] && children.style[".dropdown-menu"].fontFamily) {
                container.push(children.style[".dropdown-menu"].fontFamily);
            }
        }

        if (children.children && Array.isArray(children.children)) {
            children.children.map(extract);
        }
    }

    if (parent.containers && Array.isArray(parent.containers)) {
        parent.containers.map(extract);
    }

    return uniqueWebFonts(container);
}

export function uniqueWebFonts(allFonts) {
    const cssFonts = new Set(CSS_SAFE_FONTS.map(font => font.value));

    return [...new Set(allFonts.filter(x => !cssFonts.has(x)))];
}

export function flattenTree(tree){
  let flattenedTree = []
 
  function flatten(item){
    flattenedTree.push(item)
    if(item.children && item.children.length > 0 ){
      item.children.forEach(flatten)
    }
  }
  tree.forEach(flatten);
  return flattenedTree
}



// WEBPACK FOOTER //
// ./src/utils/Utils.js